import type { TFunction } from 'i18next';
import type {
  AcceptedBy,
  ArchiveCheck,
  ArchiveSummary,
  DocumentedResidualDecision,
  PlanOwner,
  ResidualDecision,
  ResidualDecisionType,
  ResidualRisk,
  ResidualRiskRequest,
  SafetyRepMissing,
  ShareKind,
  WorkEnvironmentArchive,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import { formatDay, fullDate } from '../HRDashboard/hmsFormat';
import { missingSuffix } from './safetyRepView';

/*
 * Which risks remain, what the checklist requires and whether the work can be archived is
 * decided in the API. What is here picks what the page shows, and checks the decision form
 * while it is filled in.
 */

export const RESIDUAL_DECISIONS: ResidualDecisionType[] = [
  'PLAN',
  'NEXT_SURVEY',
  'ACCEPT',
];

/**
 * Who can own a later survey, and who can accept a risk. Without a safety representative, the API
 * leaves them out. The next survey is one day and one owner for every area left to it, no later
 * than HR's interval.
 */
export type ResidualChoices = Pick<
  WorkEnvironmentArchive,
  'owners' | 'acceptedBy' | 'nextSurvey' | 'latestNextSurveyBy'
>;

type NextSurveyChoice = Pick<ResidualChoices, 'nextSurvey' | 'latestNextSurveyBy'>;

/** Other areas are left to the next survey already, so this one follows their day and owner */
export const followsNextSurvey = (
  risk: Pick<ResidualRisk, 'decision'>,
  { nextSurvey }: NextSurveyChoice,
) =>
  Boolean(nextSurvey) &&
  !(risk.decision?.decision === 'NEXT_SURVEY' && nextSurvey?.areas === 1);

/** «Senest 1. oktober 2027. Ansvarlig: …» */
export const nextSurveyText = (
  nextSurvey: NonNullable<ResidualChoices['nextSurvey']>,
  t: TFunction,
  language: string,
) => ({
  date: fullDate(nextSurvey.nextSurveyBy, language),
  owner: nextSurvey.owner?.email ?? t('workEnvironmentArchive.residual.noOwner'),
  count: nextSurvey.areas,
});

/** The error under a field, with the latest day HR allows when the day is wrong */
export const residualErrorText = (
  field: ResidualField,
  latest: string | null | undefined,
  t: TFunction,
  language: string,
) =>
  field === 'nextSurveyBy' && latest
    ? t('workEnvironmentArchive.residual.errors.nextSurveyByUntil', {
        date: fullDate(latest, language),
      })
    : t(`workEnvironmentArchive.residual.errors.${field}`);

/** Archived after the evaluation, as opposed to archived at the risk step with too few answers */
export const archivedAfterEvaluation = (
  survey: Pick<WorkEnvironmentSurvey, 'status' | 'actionPlanOpenedAt'>,
) =>
  survey.status === WorkEnvironmentSurveyStatus.ARCHIVED &&
  Boolean(survey.actionPlanOpenedAt);

export const residualProgress = (risks: ResidualRisk[]) => ({
  done: risks.filter((risk) => risk.decision).length,
  total: risks.length,
});

export const residualReasonText = (risk: ResidualRisk, t: TFunction) =>
  t(`workEnvironmentArchive.residual.reasons.${risk.reason}`, {
    before: t(
      `workEnvironmentRisk.matrix.levels.${risk.before.level}`,
    ).toLowerCase(),
  });

export const ownerLabel = (
  owner: Pick<PlanOwner, 'email' | 'role'>,
  t: TFunction,
) =>
  t(`workEnvironmentPlan.builder.ownerRole.${owner.role}`, {
    email: owner.email,
  });

/** What was decided, in one sentence, as in the list and in the documentation */
export const decisionText = (
  decision: Pick<
    ResidualDecision,
    'decision' | 'nextSurveyBy' | 'acceptedBy' | 'reason'
  > & {
    owner: string | null;
  },
  t: TFunction,
  language: string,
) =>
  t(`workEnvironmentArchive.residual.decided.${decision.decision}`, {
    date: decision.nextSurveyBy
      ? fullDate(decision.nextSurveyBy, language)
      : '',
    owner: decision.owner ?? '',
    by: decision.acceptedBy
      ? t(
          `workEnvironmentArchive.residual.acceptedByText.${decision.acceptedBy}`,
        )
      : '',
    reason: decision.reason ?? '',
  });

/** A decision that was replaced, with when and by whom, as in the list and in the documentation */
export const earlierDecisionText = (
  decision: DocumentedResidualDecision,
  t: TFunction,
  language: string,
) =>
  t(
    decision.decidedBy
      ? 'workEnvironmentArchive.residual.earlierItem'
      : 'workEnvironmentArchive.residual.earlierItemUnknown',
    {
      date: fullDate(decision.decidedAt, language),
      by: decision.decidedBy ?? '',
      decision: decisionText(decision, t, language),
    },
  );

/** The decisions as the documentation keeps them, with the owner by email */
export const documentedDecision = (
  decision: Omit<ResidualDecision, 'earlier'>,
): DocumentedResidualDecision => ({
  ...decision,
  owner: decision.owner?.email ?? null,
});

/** The choices that need a reason. Each keeps its own, as why a risk can wait is not why it is accepted. */
export type ReasonedDecision = Exclude<ResidualDecisionType, 'PLAN'>;

export type ResidualDraft = {
  decision: ResidualDecisionType | null;
  nextSurveyBy: string;
  ownerId: number | null;
  acceptedBy: AcceptedBy | null;
  reasons: Record<ReasonedDecision, string>;
};

export type ResidualField =
  | 'decision'
  | 'nextSurveyBy'
  | 'owner'
  | 'acceptedBy'
  | 'reason';

/** A new choice of the next survey starts from the day the other areas share, or HR's latest day */
export const residualDraft = (
  decision: ResidualDecision | null,
  { nextSurvey, latestNextSurveyBy }: NextSurveyChoice = {},
): ResidualDraft => ({
  decision: decision?.decision ?? null,
  nextSurveyBy:
    decision?.nextSurveyBy ?? nextSurvey?.nextSurveyBy ?? latestNextSurveyBy ?? '',
  ownerId: decision?.owner?.id ?? nextSurvey?.owner?.id ?? null,
  acceptedBy: decision?.acceptedBy ?? null,
  reasons: {
    NEXT_SURVEY: decision?.decision === 'NEXT_SURVEY' ? (decision.reason ?? '') : '',
    ACCEPT: decision?.decision === 'ACCEPT' ? (decision.reason ?? '') : '',
  },
});

/** Tomorrow at the earliest, and no later than HR's interval */
export type DayLimits = { today: string; latest?: string | null };

/** The day and owner of the next survey, as one form for every area left to it */
export const nextSurveyErrors = (
  choice: Pick<ResidualDraft, 'nextSurveyBy' | 'ownerId'>,
  { today, latest }: DayLimits,
): ResidualField[] => [
  ...(choice.nextSurveyBy > today && (!latest || choice.nextSurveyBy <= latest)
    ? []
    : ['nextSurveyBy' as const]),
  ...(choice.ownerId ? [] : ['owner' as const]),
];

const FIELD_RULES: Record<
  ResidualDecisionType,
  (draft: ResidualDraft, limits: DayLimits) => ResidualField[]
> = {
  PLAN: () => [],
  NEXT_SURVEY: (draft, limits) => [
    ...nextSurveyErrors(draft, limits),
    ...(draft.reasons.NEXT_SURVEY.trim() ? [] : ['reason' as const]),
  ],
  ACCEPT: (draft) => [
    ...(draft.acceptedBy ? [] : ['acceptedBy' as const]),
    ...(draft.reasons.ACCEPT.trim() ? [] : ['reason' as const]),
  ],
};

/** The same rules as the API, so the form can say what is missing before it is saved */
export const draftErrors = (
  draft: ResidualDraft,
  limits: DayLimits,
): ResidualField[] =>
  draft.decision ? FIELD_RULES[draft.decision](draft, limits) : ['decision'];

/** An area that follows the next survey of the others takes their day and owner */
export const withNextSurveyOf = (
  draft: ResidualDraft,
  nextSurvey: ResidualChoices['nextSurvey'],
): ResidualDraft =>
  nextSurvey
    ? { ...draft, nextSurveyBy: nextSurvey.nextSurveyBy, ownerId: nextSurvey.owner?.id ?? null }
    : draft;

export const toRequest = (draft: ResidualDraft): ResidualRiskRequest => ({
  decision: draft.decision ?? 'PLAN',
  ...(draft.decision === 'NEXT_SURVEY' && {
    nextSurveyBy: draft.nextSurveyBy,
    ownerId: draft.ownerId,
    reason: draft.reasons.NEXT_SURVEY.trim(),
  }),
  ...(draft.decision === 'ACCEPT' && {
    acceptedBy: draft.acceptedBy,
    reason: draft.reasons.ACCEPT.trim(),
  }),
});

export const residualFieldId = (key: string, field: ResidualField) =>
  `residual-${key}-${field}`;

export const residualItemId = (key: string) => `residual-${key}`;

const day = (value: string | null, t: TFunction, language: string) =>
  value ? formatDay(value, t, language) : '';

const signatureNoteKey = (
  signature: NonNullable<ArchiveSummary['signature']>,
  noRep: string,
) => {
  if (signature.status === 'DEVIATION') return `DEVIATION${noRep}`;
  if (signature.status === 'SIGNED' && signature.comment)
    return 'SIGNEDWithComment';
  return signature.status;
};

/** Where the signature of the safety representative is. Without one, the deviation is what remains. */
export const signatureNote = (
  signature: ArchiveSummary['signature'],
  missing: SafetyRepMissing | null,
  t: TFunction,
  language: string,
) => {
  const noRep = missingSuffix(missing);
  return signature
    ? t(
        `workEnvironmentArchive.checklist.notes.safetyRep.${signatureNoteKey(signature, noRep)}`,
        {
          date: day(
            signature.respondedAt ?? signature.requestedAt,
            t,
            language,
          ),
          comment: signature.comment ?? '',
        },
      ).trim()
    : t(`workEnvironmentArchive.checklist.notes.safetyRep.none${noRep}`);
};

/**
 * The team leader asks until the documentation is signed, and asks again after a return.
 * Without a safety representative there is no one to ask, only the deviation to register.
 * The API says when a deviation can be registered.
 */
export const signatureActions = (
  signature: ArchiveSummary['signature'],
  { hasSafetyRep, canRegisterDeviation }: Pick<WorkEnvironmentArchive, 'hasSafetyRep' | 'canRegisterDeviation'>,
) => ({
  ask:
    hasSafetyRep &&
    (!signature ||
      signature.status === 'RETURNED' ||
      signature.status === 'DEVIATION' ||
      signature.status === 'WITHDRAWN'),
  remind: hasSafetyRep && signature?.status === 'REQUESTED',
  withdraw: signature?.status === 'REQUESTED',
  deviation: canRegisterDeviation && signature?.status !== 'DEVIATION',
});

type Check = WorkEnvironmentArchive['checklist'][number];

/**
 * A deviation documents the work, but says the safety representative did not take part. Without anyone who could
 * sign, it says why instead of naming a safety representative.
 */
export const checkLabel = (
  check: Check,
  summary: Pick<ArchiveSummary, 'signature'>,
  t: TFunction,
  missing: SafetyRepMissing | null = null,
) =>
  t(
    `workEnvironmentArchive.checklist.labels.${check.key}${
      check.key === 'safetyRep' && summary.signature?.status === 'DEVIATION'
        ? `Deviation${missingSuffix(missing)}`
        : ''
    }`,
  );

/** What each check shows, from what the API found */
const NOTES: Record<
  ArchiveCheck,
  (
    summary: ArchiveSummary,
    t: TFunction,
    language: string,
    check: Check,
    missing: SafetyRepMissing | null,
  ) => string
> = {
  survey: (summary, t, language) =>
    t('workEnvironmentArchive.checklist.notes.survey', {
      responses: summary.responses,
      expected: summary.expected,
      date: day(summary.surveyedAt, t, language),
    }),
  assessment: (summary, t, language) =>
    summary.assessment
      ? t('workEnvironmentArchive.checklist.notes.assessment', {
          count: summary.areas,
          date: day(summary.assessment.at, t, language),
        })
      : t('workEnvironmentArchive.checklist.notes.assessmentMissing'),
  participation: (summary, t) =>
    t('workEnvironmentArchive.checklist.notes.participation', {
      proposals: summary.proposals,
      voters: summary.voters,
      expected: summary.expected,
    }),
  safetyRep: (summary, t, language, _done, missing) =>
    [
      signatureNote(summary.signature, missing, t, language),
      ...summary.deviations.map((reason) =>
        t('workEnvironmentArchive.checklist.notes.deviation', { reason }),
      ),
    ].join(' '),
  measuresPlanned: (summary, t, _language, { done, gap }) => {
    if (done)
      return t('workEnvironmentArchive.checklist.notes.measuresPlanned', {
        count: summary.measures,
      });
    return gap === 'noMeasures'
      ? t('workEnvironmentArchive.checklist.notes.measuresPlannedNone')
      : t('workEnvironmentArchive.checklist.notes.measuresPlannedMissing');
  },
  measuresDone: (summary, t, _language, { done }) =>
    [
      t('workEnvironmentArchive.checklist.notes.measuresDone', {
        done: summary.measuresDone,
        count: summary.measures,
      }),
      ...(done
        ? []
        : [t('workEnvironmentArchive.checklist.notes.measuresLeft')]),
    ].join(' '),
  evaluated: (summary, t, language) =>
    t('workEnvironmentArchive.checklist.notes.evaluated', {
      date: day(summary.evaluatedAt, t, language),
    }),
  combined: (summary, t, language, { done }) =>
    done
      ? t(
          summary.assessment?.participation === 'SAFETY_REP'
            ? 'workEnvironmentArchive.checklist.notes.combinedWithSafetyRep'
            : 'workEnvironmentArchive.checklist.notes.combined',
          { date: day(summary.assessment?.at ?? null, t, language) },
        )
      : t('workEnvironmentArchive.checklist.notes.combinedMissing'),
  shared: (summary, t, language, { done }) =>
    done
      ? t('workEnvironmentArchive.checklist.notes.shared', {
          result: day(summary.sharedResultAt, t, language),
          plan: day(summary.sharedPlanAt, t, language),
        })
      : t('workEnvironmentArchive.checklist.notes.sharedMissing'),
  residual: () => '',
};

export const checkNote = (
  check: Check,
  archive: Pick<WorkEnvironmentArchive, 'summary' | 'residual' | 'safetyRepMissing'>,
  t: TFunction,
  language: string,
) =>
  check.key === 'residual'
    ? t(
        'workEnvironmentArchive.checklist.notes.residual',
        residualProgress(archive.residual),
      )
    : NOTES[check.key](archive.summary, t, language, check, archive.safetyRepMissing);

/** What the team has not got yet, so it can be sent from the checklist */
export const missingShares = (summary: ArchiveSummary): ShareKind[] => [
  ...(summary.sharedResultAt ? [] : ['RESULT' as const]),
  ...(summary.sharedPlanAt ? [] : ['PLAN' as const]),
];

const SIGNATURE = 'workEnvironmentArchive.checklist.signature';

const listed = (emails: string[]) => ({ emails: emails.join(', '), count: emails.length });

/**
 * Who signs for the team, and why when it is the main safety representative. The team leader
 * cannot go past them (arbeidsmiljøloven § 6-2), so they need to know who they wait for.
 */
export const whoSignsText = (
  { actingSafetyReps, takenOverBy }: Pick<WorkEnvironmentArchive, 'actingSafetyReps' | 'takenOverBy'>,
  t: TFunction,
) => {
  if (takenOverBy && takenOverBy.length > 0) return t('workEnvironmentRisk.takenOver', listed(takenOverBy));
  return actingSafetyReps
    ? t(`${SIGNATURE}.${actingSafetyReps.actsAs}Signs`, listed(actingSafetyReps.emails))
    : null;
};

/**
 * The way forward while the safety representative has not answered, as only they can let the work be
 * archived. A team covered by the main safety representative can also get its own.
 */
export const waitingText = (
  signature: ArchiveSummary['signature'],
  { hasSafetyRep, actingSafetyReps }: Pick<WorkEnvironmentArchive, 'hasSafetyRep' | 'actingSafetyReps'>,
  t: TFunction,
) =>
  hasSafetyRep && signature?.status === 'REQUESTED'
    ? t(actingSafetyReps?.actsAs === 'main' ? `${SIGNATURE}.waitingMain` : `${SIGNATURE}.waiting`)
    : null;

export const CHECKLIST_ID = 'archive-checklist';

const gapText = (check: Check, archive: WorkEnvironmentArchive, t: TFunction, language: string) =>
  [
    `${checkLabel(check, archive.summary, t, archive.safetyRepMissing)}: ${checkNote(check, archive, t, language)}`,
    check.key === 'safetyRep' && waitingText(archive.summary.signature, archive, t),
  ]
    .filter(Boolean)
    .join(' ');

/** The error summary when archiving is tried too early, each item linking to where it is fixed */
export const missingForArchive = (
  archive: WorkEnvironmentArchive,
  t: TFunction,
  language: string,
) => [
  ...archive.checklist
    .filter(
      (check) => check.required && !check.done && check.key !== 'residual',
    )
    .map((check) => ({
      key: check.key,
      href: `#${CHECKLIST_ID}`,
      text: gapText(check, archive, t, language),
    })),
  ...archive.residual
    .filter((risk) => !risk.decision)
    .map((risk) => ({
      key: risk.key,
      href: `#${residualItemId(risk.key)}`,
      text: t('workEnvironmentArchive.checklist.residualMissing', {
        name: risk.name,
      }),
    })),
];
